import { useState } from "react"
import type { Table, Column } from "@tanstack/react-table"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Search, Plus, X } from "lucide-react"
import { FilterInput } from "./data-grid"
import { parseDateOnly } from "@/lib/utils"
import { format } from "date-fns"

interface FilterBarProps<T> {
  table: Table<T> | null
  globalFilter: string
  onGlobalFilterChange: (value: string) => void
}

function isFilterActive(column: Column<any, any>): boolean {
  const v = column.getFilterValue()
  if (v == null || v === "") return false
  if (Array.isArray(v)) return v.length > 0
  if (typeof v === "object") {
    const o = v as { value?: unknown; value2?: unknown }
    const hasValue = "value" in o && o.value !== null && o.value !== undefined && o.value !== ""
    const hasValue2 = "value2" in o && o.value2 !== null && o.value2 !== undefined && o.value2 !== ""
    return hasValue || hasValue2
  }
  return true
}

function formatDateShort(iso: string): string {
  const d = parseDateOnly(iso)
  return d ? format(d, "MMM d, yyyy") : iso
}

function summarizeFilter(column: Column<any, any>): string {
  const v = column.getFilterValue()
  const meta = (column.columnDef.meta as any) ?? {}

  if (v == null || v === "") return ""

  if (Array.isArray(v)) {
    if (v.length === 0) return ""
    if (v.length === 1) return String(v[0])
    return `${v.length} selected`
  }

  if (meta.type === "date" && typeof v === "object" && v !== null) {
    const o = v as any
    const glyph: Record<string, string> = {
      eq: "=", ne: "≠", lt: "<", gt: ">", lte: "≤", gte: "≥", between: "↔",
    }
    const g = glyph[o.op] ?? "="
    if (o.op === "between") {
      if (!o.value && !o.value2) return ""
      const a = o.value ? formatDateShort(o.value) : "…"
      const b = o.value2 ? formatDateShort(o.value2) : "…"
      return `${a} – ${b}`
    }
    return o.value ? `${g} ${formatDateShort(o.value)}` : ""
  }

  if (meta.type === "date" && typeof v === "string") return formatDateShort(v)

  if (meta.type === "boolean") return v ? "Yes" : "No"

  return String(v)
}

function FilterChip({ column, table }: { column: Column<any, any>; table: Table<any> }) {
  const [open, setOpen] = useState(false)
  const header = column.columnDef.header
  const label = typeof header === "string" ? header : column.id
  const summary = summarizeFilter(column)

  const handleRemove = () => {
    column.setFilterValue(undefined)
  }

  return (
    <div className="inline-flex items-stretch h-8 rounded-md border border-slate-300 bg-white text-xs overflow-hidden hover:bg-slate-50 transition-colors">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5"
          >
            <span className="font-medium text-slate-700">{label}:</span>
            <span className="text-slate-600 truncate max-w-[160px]">{summary || "…"}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[280px] p-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {label}
            </label>
            <FilterInput column={column} table={table} expanded />
          </div>
        </PopoverContent>
      </Popover>
      <button
        type="button"
        onClick={handleRemove}
        className="px-1.5 text-slate-400 hover:text-red-600 border-l border-slate-200"
        aria-label={`Remove ${label} filter`}
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  )
}

function AddFilterButton({ columns, table }: { columns: Column<any, any>[]; table: Table<any> }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [selectedColumnId, setSelectedColumnId] = useState<string | null>(null)

  const reset = () => {
    setSelectedColumnId(null)
    setSearch("")
  }

  const handleOpenChange = (o: boolean) => {
    setOpen(o)
    if (!o) reset()
  }

  const filtered = columns.filter((c) => {
    const header = c.columnDef.header
    const label = typeof header === "string" ? header : c.id
    return label.toLowerCase().includes(search.toLowerCase())
  })

  const selectedColumn = selectedColumnId ? table.getColumn(selectedColumnId) ?? null : null
  const selectedHeader = selectedColumn?.columnDef.header
  const selectedLabel = typeof selectedHeader === "string" ? selectedHeader : selectedColumn?.id

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs font-normal text-slate-600 border-dashed">
          <Plus className="w-3.5 h-3.5" />
          Add filter
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[280px] p-0">
        {!selectedColumn ? (
          <>
            <div className="p-1">
              <Input
                placeholder="Search columns..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 text-xs"
                autoFocus
              />
            </div>
            <div className="max-h-64 overflow-y-auto p-1">
              {filtered.length === 0 ? (
                <div className="px-2 py-3 text-xs text-slate-400 text-center">No columns found</div>
              ) : (
                filtered.map((col) => {
                  const header = col.columnDef.header
                  const label = typeof header === "string" ? header : col.id
                  return (
                    <button
                      key={col.id}
                      type="button"
                      className="flex items-center w-full px-2 py-1.5 text-xs rounded-sm hover:bg-slate-100 transition-colors text-left"
                      onClick={() => setSelectedColumnId(col.id)}
                    >
                      {label}
                    </button>
                  )
                })
              )}
            </div>
          </>
        ) : (
          <div className="p-3 flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={reset}
                className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700"
              >
                ← Back
              </button>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                {selectedLabel}
              </span>
            </div>
            <FilterInput column={selectedColumn} table={table} expanded />
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

export function FilterBar<T>({ table, globalFilter, onGlobalFilterChange }: FilterBarProps<T>) {
  if (!table) return null

  const filterableColumns = table.getAllLeafColumns().filter((col) => {
    if (!col.getCanFilter()) return false
    const meta = (col.columnDef.meta as any) ?? {}
    return meta.type != null
  })

  const activeColumns = filterableColumns.filter((col) => isFilterActive(col))
  const availableColumns = filterableColumns.filter((col) => !isFilterActive(col))

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm px-3 py-2 mb-3 flex items-center gap-2">
      <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
        {activeColumns.map((col) => (
          <FilterChip key={col.id} column={col} table={table} />
        ))}

        {availableColumns.length > 0 && (
          <AddFilterButton columns={availableColumns} table={table} />
        )}
      </div>

      <div className="relative w-64 flex-shrink-0">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search all columns..."
          value={globalFilter}
          onChange={(e) => onGlobalFilterChange(e.target.value)}
          className="pl-9 h-9 bg-white border-slate-300"
        />
      </div>
    </div>
  )
}
